import { deburr } from 'lodash';

import {
  type Item,
  SelectionCategories,
  type SelectionList,
  type SelectionListIdNames,
  type TreeItem,
} from '@revelio/iso-utility';

import { getMatchQuality, MatchQuality } from './match-quality';

const ALL_ROLE_FILTER_IDS: SelectionCategories[] = [
  SelectionCategories.JOB_CATEGORY,
  SelectionCategories.ROLE_K7,
  SelectionCategories.ROLE_K50,
  SelectionCategories.ROLE_K150,
  SelectionCategories.ROLE_K300,
  SelectionCategories.ROLE_K500,
  SelectionCategories.ROLE_K1250,
  SelectionCategories.ROLE_K1500,
  SelectionCategories.ROLE_K5000,
  SelectionCategories.ROLE_K15000,
];

// Scoring constants and helpers
type MatchType = 'direct' | 'suggested';

export const POSITION_SCORES = {
  startOfString: 30,
  startOfWord: 20,
  afterPunctuation: 10,
} as const;

export const SUGGESTED_POSITION_MULTIPLIER = 0.7;
export const DIRECT_MATCH_BONUS = 1000;

const LEAF_BASE_SCORES: Record<MatchType, Record<MatchQuality, number>> = {
  direct: {
    [MatchQuality.EXACT]: 15000,
    [MatchQuality.PARTIAL]: 100,
    [MatchQuality.NONE]: 0,
  },
  suggested: {
    [MatchQuality.EXACT]: 75,
    [MatchQuality.PARTIAL]: 50,
    [MatchQuality.NONE]: 0,
  },
};

export const getBaseLeafScore = (
  matchType: MatchType,
  quality: MatchQuality
): number => LEAF_BASE_SCORES[matchType][quality];

const PARENT_BASE_SCORES: Record<MatchType, Record<MatchQuality, number>> = {
  direct: {
    [MatchQuality.EXACT]: 15,
    [MatchQuality.PARTIAL]: 10,
    [MatchQuality.NONE]: 0,
  },
  suggested: {
    [MatchQuality.EXACT]: 8,
    [MatchQuality.PARTIAL]: 5,
    [MatchQuality.NONE]: 0,
  },
};

export const getBaseParentScore = (
  matchType: MatchType,
  quality: MatchQuality
): number => PARENT_BASE_SCORES[matchType][quality];

export const getPositionBonusFirstOccurrence = (
  text: string,
  search: string
): number => {
  const normalizedText = text.toLowerCase().trim();
  const normalizedSearch = search.toLowerCase().trim();
  if (!normalizedText.includes(normalizedSearch)) return 0;

  if (normalizedText.startsWith(normalizedSearch)) {
    return POSITION_SCORES.startOfString;
  }
  if (normalizedText.includes(` ${normalizedSearch}`)) {
    return POSITION_SCORES.startOfWord;
  }

  const index = normalizedText.indexOf(normalizedSearch);
  if (index > 0) {
    const beforeChar = normalizedText.charAt(index - 1) || ' ';
    if (/[^A-Za-z0-9_\s]/.test(beforeChar)) {
      return POSITION_SCORES.afterPunctuation;
    }
  }

  return 0;
};

export const getPositionBonusAnyOccurrence = (
  text: string,
  search: string
): number => {
  const normalizedText = text.toLowerCase().trim();
  const normalizedSearch = search.toLowerCase().trim();
  if (!normalizedText.includes(normalizedSearch)) return 0;

  if (normalizedText.startsWith(normalizedSearch)) {
    return POSITION_SCORES.startOfString;
  }
  if (normalizedText.includes(` ${normalizedSearch}`)) {
    return POSITION_SCORES.startOfWord;
  }

  let index = normalizedText.indexOf(normalizedSearch);
  while (index !== -1) {
    if (index > 0) {
      const beforeChar = normalizedText.charAt(index - 1) || ' ';
      if (/[^A-Za-z0-9_\s]/.test(beforeChar)) {
        return POSITION_SCORES.afterPunctuation;
      }
    }
    index = normalizedText.indexOf(normalizedSearch, index + 1);
  }

  return 0;
};

export const normalizeToWords = (value: string): string[] => {
  const normalized = deburr(value.toLowerCase().trim());
  return normalized.split(/\s+/).filter(Boolean);
};

export const isLenientWordMatch = (
  searchWord: string,
  valueWord: string
): boolean => {
  if (!searchWord || !valueWord) return false;
  if (searchWord === valueWord) return true;
  if (valueWord.startsWith(searchWord)) return true;
  if (valueWord.endsWith(searchWord)) return true;
  if (valueWord === `${searchWord}s` || `${valueWord}s` === searchWord) {
    return true;
  }
  if (valueWord === `${searchWord}es` || `${valueWord}es` === searchWord) {
    return true;
  }
  return false;
};

export const areAllSearchWordsLenientMatch = (
  search: string,
  text: string
): boolean => {
  const searchWords = normalizeToWords(search);
  const valueWords = normalizeToWords(text);
  if (searchWords.length === 0 || valueWords.length === 0) return false;
  return searchWords.every((searchWord) =>
    valueWords.some((valueWord) => isLenientWordMatch(searchWord, valueWord))
  );
};

export const getSuggestedMatchQuality = (
  values: string[],
  search: string
): MatchQuality => {
  if (!values?.length) return MatchQuality.NONE;

  const normalizedSearch = search.toLowerCase().trim();
  const searchWords = normalizedSearch
    .split(/\s+/)
    .filter((word) => word.length > 0);
  let hasPartialMatch = false;

  for (const value of values) {
    const normalizedValue = value.toLowerCase().trim();
    if (!normalizedValue.includes(normalizedSearch)) continue;
    hasPartialMatch = true;

    if (searchWords.length === 1) {
      const searchIndex = normalizedValue.indexOf(normalizedSearch);
      if (searchIndex !== -1) {
        const beforeChar =
          searchIndex === 0
            ? ' '
            : normalizedValue.charAt(searchIndex - 1) || ' ';
        const afterIndex = searchIndex + normalizedSearch.length;
        const afterChar =
          afterIndex >= normalizedValue.length
            ? ' '
            : normalizedValue.charAt(afterIndex) || ' ';
        const isWordBoundary = (char: string) => /[\s\W]/.test(char);
        if (isWordBoundary(beforeChar) && isWordBoundary(afterChar)) {
          return MatchQuality.EXACT;
        }
      }
    } else {
      const valueWords = normalizedValue.split(/\s+/);
      const allWordsMatch = searchWords.every((searchWord) =>
        valueWords.some(
          (valueWord) =>
            valueWord === searchWord ||
            isLenientWordMatch(searchWord, valueWord)
        )
      );
      if (allWordsMatch) return MatchQuality.EXACT;
    }
  }

  return hasPartialMatch ? MatchQuality.PARTIAL : MatchQuality.NONE;
};

export const maxQuality = (qualities: MatchQuality[]): MatchQuality => {
  if (qualities.includes(MatchQuality.EXACT)) return MatchQuality.EXACT;
  if (qualities.includes(MatchQuality.PARTIAL)) return MatchQuality.PARTIAL;
  return MatchQuality.NONE;
};

type TreeItemWithClosenessScore = TreeItem & {
  closeness_score: number | undefined;
};

export const findSelectionListItemByLabel = ({
  labelToFind,
  selectionList,
  returnMultipleResults = false,
}: {
  labelToFind: string;
  selectionList: SelectionList;
  returnMultipleResults?: boolean;
}): TreeItemWithClosenessScore[] | null => {
  const search = labelToFind?.trim();
  if (!search) return null;

  const searchWords = normalizeToWords(search);

  const isRoleOrSkill = [
    ...ALL_ROLE_FILTER_IDS,
    SelectionCategories.SKILL_K25,
    SelectionCategories.SKILL_K50,
    SelectionCategories.SKILL_K75,
    SelectionCategories.SKILL_K700,
    SelectionCategories.SKILL_K3000,
    SelectionCategories.SKILL_K15000,
  ].includes(selectionList.id as SelectionCategories);

  const scored = selectionList.value
    .map((item) => {
      const nameCandidates: string[] = [
        (item as Item).longName || '',
        (item as Item).shortName || '',
        (item as Item).label || '',
      ].filter(Boolean);

      const directQualities = nameCandidates.map((name) =>
        getMatchQuality(name, search)
      );
      let directQuality = maxQuality(directQualities);
      if (directQuality !== MatchQuality.EXACT && searchWords.length > 0) {
        const candidateName = nameCandidates[0] || '';
        if (areAllSearchWordsLenientMatch(search, candidateName)) {
          directQuality = MatchQuality.EXACT;
        }
      }

      const suggestedValues: string[] = [];
      if (isRoleOrSkill) {
        if (Array.isArray((item as Record<string, unknown>).rawTitles)) {
          suggestedValues.push(
            ...(((item as Record<string, unknown>).rawTitles as string[]) || [])
          );
        }
        const topSkills = (item as Record<string, unknown>).topSkills as
          | string
          | string[]
          | undefined;
        if (typeof topSkills === 'string') {
          suggestedValues.push(
            ...topSkills
              .split(', ')
              .map((s) => s.trim())
              .filter(Boolean)
          );
        } else if (Array.isArray(topSkills)) {
          suggestedValues.push(...topSkills);
        }
      }

      const suggestedQuality = getSuggestedMatchQuality(
        suggestedValues,
        search
      );

      const directScore = getBaseLeafScore('direct', directQuality);
      const suggestedScore =
        directQuality === MatchQuality.NONE
          ? getBaseLeafScore('suggested', suggestedQuality)
          : 0;

      let totalScore = directScore + suggestedScore;
      const usedLenient =
        directQuality === MatchQuality.EXACT &&
        !directQualities.includes(MatchQuality.EXACT);
      if (usedLenient) totalScore -= 5;
      if (directQuality !== MatchQuality.NONE) totalScore += DIRECT_MATCH_BONUS;

      if (directQuality !== MatchQuality.NONE && nameCandidates.length) {
        const maxDirectBonus = Math.max(
          ...nameCandidates.map((name) =>
            getPositionBonusFirstOccurrence(name, search)
          )
        );
        totalScore += maxDirectBonus;
      } else if (
        directQuality === MatchQuality.NONE &&
        suggestedQuality !== MatchQuality.NONE &&
        suggestedValues.length
      ) {
        const maxSuggestedBonus = Math.max(
          ...suggestedValues.map((val) =>
            getPositionBonusFirstOccurrence(val, search)
          )
        );
        totalScore += Math.floor(
          maxSuggestedBonus * SUGGESTED_POSITION_MULTIPLIER
        );
      }

      if (totalScore === 0) return null;

      const parentRawId =
        ((item as Record<string, unknown>).parentId as string | undefined) ??
        (selectionList.parent
          ? (item as Record<string, unknown>)[selectionList.parent as string]
          : undefined);

      const closeness_score = 1 / (1 + totalScore);

      const treeItem: TreeItemWithClosenessScore = {
        item: item as Item,
        id: selectionList.id + '.' + (item as Item).id,
        parentId:
          parentRawId && selectionList.parent
            ? selectionList.parent + '.' + parentRawId
            : undefined,
        children: [],
        selectionListId: selectionList.id as SelectionListIdNames,
        closeness_score,
      };

      return treeItem;
    })
    .filter((v): v is TreeItemWithClosenessScore => v != null)
    .sort((a, b) => (a.closeness_score || 1) - (b.closeness_score || 1));

  if (scored.length === 0) return null;
  if (returnMultipleResults) return scored;
  const top = scored[0];
  if (!top) return null;
  const EPSILON = 1e-12;
  const topGroup = scored.filter(
    (s) =>
      Math.abs((s.closeness_score || 1) - (top.closeness_score || 1)) <=
        EPSILON && s.selectionListId === top.selectionListId
  );
  return topGroup.length ? topGroup : [top];
};
